import React from 'react';
import { createPortal } from 'react-dom';
import AppStatic, { isStudentsBuild } from '../config/AppStatic';
import Card from './ui/Card';
import Button from './ui/Button';
import MultiSelectDropdown from './ui/MultiSelectDropdown'; // Keep this import
import { HiAdjustmentsHorizontal } from 'react-icons/hi2';
import MobileFilterButtons from './MobileFilterButtons';
import StudentGroupSelectionModal from './StudentGroupSelectionModal';
import TeacherSelectionModal from './TeacherSelectionModal';

// Chevron icon matching MultiSelectDropdown
const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

function ScheduleFilter({
  // Filter options
  teachers,
  courses,
  groups, // Keep groups prop
  // Current filter values
  selectedTeacher,
  selectedCourse,
  selectedGroups, // Keep selectedGroups prop
  onTeacherChange,
  onCourseChange,
  onGroupChange, // Keep onGroupChange prop
  onClearFilters,
  isPublicView = false, // NEW: Add isPublicView prop
  onOpenComplexFilter, // NEW: Add complex filter handler
  // Complex filter data
  complexFilterGroups = [],
  complexFilterTeachers = [],
  complexFilterSubjects = [],
  complexFilterRooms = [],
  // Visibility controls
  showTeacher = true,
  showCourse = !isPublicView,
  showGroups = true,
  compactMobile = false
}) {
  // Mobile modal state
  const [isStudentModalOpen, setIsStudentModalOpen] = React.useState(false);
  const [isTeacherModalOpen, setIsTeacherModalOpen] = React.useState(false);
  const [isTeacherDropdownOpen, setIsTeacherDropdownOpen] = React.useState(false);
  const [teacherSearchTerm, setTeacherSearchTerm] = React.useState('');
  const [teacherPanelStyle, setTeacherPanelStyle] = React.useState({});
  const teacherDropdownRef = React.useRef(null);
  const teacherTriggerRef = React.useRef(null);
  // Ensure callbacks are functions to prevent errors
  const handleTeacherChange = (e) => typeof onTeacherChange === 'function' && onTeacherChange(e.target.value);
  const handleCourseChange = (e) => typeof onCourseChange === 'function' && onCourseChange(e.target.value);

  // Handle group changes based on view mode
  const handleGroupsChange = (value) => {
    if (typeof onGroupChange === 'function') {
      if (isPublicView) {
        // In public view, value is a single string from select dropdown
        onGroupChange(value);
      } else {
        // In admin view, value is an array from MultiSelectDropdown
        onGroupChange(value);
      }
    }
  };

  const handleClearFilters = () => typeof onClearFilters === 'function' && onClearFilters();
  const handleOpenComplexFilter = () => typeof onOpenComplexFilter === 'function' && onOpenComplexFilter();

  // Mobile modal handlers
  const handleOpenStudentModal = () => setIsStudentModalOpen(true);
  const handleCloseStudentModal = () => setIsStudentModalOpen(false);
  const handleOpenTeacherModal = () => setIsTeacherModalOpen(true);
  const handleCloseTeacherModal = () => setIsTeacherModalOpen(false);

  const handleStudentGroupSelect = (selectedGroups) => {
    if (typeof onGroupChange === 'function') {
      // For public view (mobile), pass the first selected group as a string
      // For admin view, pass the array as-is
      if (isPublicView && selectedGroups && selectedGroups.length > 0) {
        onGroupChange(selectedGroups[0]); // Pass the first group as a string
      } else {
        onGroupChange(selectedGroups); // Pass the array for admin view
      }
    }
  };

  const handleTeacherSelect = (selectedTeacher) => {
    if (typeof onTeacherChange === 'function') {
      onTeacherChange(selectedTeacher);
    }
  };
  const handleDesktopTeacherSelect = (teacherName) => {
    if (typeof onTeacherChange === 'function') {
      onTeacherChange(teacherName);
    }
    setIsTeacherDropdownOpen(false);
  };

  // Ensure options are arrays before mapping
  const teacherOptions = Array.isArray(teachers) ? teachers : [];
  const courseOptions = Array.isArray(courses) ? courses : [];
  const groupOptions = Array.isArray(groups) ? groups : [];

  // Check if complex filters are active
  const complexFiltersActive = complexFilterGroups.length > 0 ||
    complexFilterTeachers.length > 0 ||
    complexFilterSubjects.length > 0 ||
    complexFilterRooms.length > 0;

  // Helper function to generate complex filter text overview
  const getComplexFilterText = () => {
    const parts = [];

    if (complexFilterGroups.length > 0) {
      const groupsText = complexFilterGroups.length > 3
        ? complexFilterGroups.slice(0, 3).join(', ') + '...'
        : complexFilterGroups.join(', ');
      parts.push(`Groups: ${groupsText}`);
    }
    if (complexFilterTeachers.length > 0) {
      const teachersText = complexFilterTeachers.length > 3
        ? complexFilterTeachers.slice(0, 3).join(', ') + '...'
        : complexFilterTeachers.join(', ');
      parts.push(`Teachers: ${teachersText}`);
    }
    if (complexFilterSubjects.length > 0) {
      const subjectsText = complexFilterSubjects.length > 3
        ? complexFilterSubjects.slice(0, 3).join(', ') + '...'
        : complexFilterSubjects.join(', ');
      parts.push(`Subjects: ${subjectsText}`);
    }
    if (complexFilterRooms.length > 0) {
      const roomsText = complexFilterRooms.length > 3
        ? complexFilterRooms.slice(0, 3).join(', ') + '...'
        : complexFilterRooms.join(', ');
      parts.push(`Rooms: ${roomsText}`);
    }

    return parts.join(' | ');
  }; // Keep group options
  const appType = AppStatic.getBuildType();
  const modernFilterControlClass = 'w-full px-4 py-3 pr-10 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] appearance-none transition focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 hover:border-gray-300';
  const modernFilterButtonClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] transition focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 hover:border-gray-300 flex items-center justify-between gap-2 text-left';
  const modernReadonlyControlClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white/95 shadow-sm text-sm text-gray-700 min-h-[48px] flex items-center';
  const teacherNames = teacherOptions
    .filter(t => t)
    .map(teacher => typeof teacher === 'string' ? teacher : teacher?.name || 'Unknown');
  const filteredTeacherNames = teacherNames.filter((name) => {
    if (!teacherSearchTerm.trim()) {
      return true;
    }
    return name.toLowerCase().includes(teacherSearchTerm.trim().toLowerCase());
  });

  const updateTeacherPanelPosition = React.useCallback(() => {
    if (!teacherTriggerRef.current) {
      return;
    }
    const rect = teacherTriggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const panelHeightEstimate = 280;
    const margin = 4;
    let top = 'auto';
    let bottom = 'auto';

    if (spaceBelow >= panelHeightEstimate || spaceBelow >= spaceAbove) {
      top = `${rect.bottom + window.scrollY + margin}px`;
    } else {
      bottom = `${window.innerHeight - rect.top - window.scrollY + margin}px`;
    }

    setTeacherPanelStyle({
      position: 'absolute',
      top,
      bottom,
      left: `${rect.left + window.scrollX}px`,
      width: `${rect.width}px`,
      zIndex: 1100
    });
  }, []);

  // Auto-open student group selection on mobile in STUDENTS app when no group is selected
  React.useEffect(() => {
    if (isPublicView && appType === 'STUDENTS') {
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      const hasGroup = Array.isArray(selectedGroups) && selectedGroups.length > 0 && selectedGroups[0];
      if (isMobile && !hasGroup && !isStudentModalOpen) {
        setIsStudentModalOpen(true);
      }
    }
  }, [isPublicView, appType, selectedGroups, isStudentModalOpen]);

  React.useEffect(() => {
    if (!isTeacherDropdownOpen) {
      setTeacherSearchTerm('');
      return;
    }

    const handleClickOutside = (event) => {
      const panelElement = document.getElementById('teacher-filter-panel');
      if (
        teacherDropdownRef.current &&
        !teacherDropdownRef.current.contains(event.target) &&
        (!panelElement || !panelElement.contains(event.target))
      ) {
        setIsTeacherDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTeacherDropdownOpen]);

  React.useLayoutEffect(() => {
    if (!isTeacherDropdownOpen) {
      return;
    }
    updateTeacherPanelPosition();
    const handleReposition = () => updateTeacherPanelPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isTeacherDropdownOpen, updateTeacherPanelPosition]);

  const teacherDropdownPanel = isTeacherDropdownOpen ? (
    <div
      id="teacher-filter-panel"
      style={teacherPanelStyle}
      className="bg-white shadow-xl rounded-xl border border-gray-200 max-h-72 overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-gray-100 bg-white">
        <input
          type="text"
          value={teacherSearchTerm}
          onChange={(e) => setTeacherSearchTerm(e.target.value)}
          placeholder="Search teachers..."
          className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          autoFocus
          aria-label="Search teachers"
        />
      </div>
      <ul className="py-1 overflow-y-auto" role="listbox" aria-label="Teacher options">
        <li
          className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault();
            handleDesktopTeacherSelect('');
          }}
          role="option"
          aria-selected={!selectedTeacher}
        >
          All Teachers
        </li>
        {filteredTeacherNames.length > 0 ? filteredTeacherNames.map((teacherName, index) => (
          <li
            key={`${teacherName}-${index}`}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer"
            onMouseDown={(e) => {
              e.preventDefault();
              handleDesktopTeacherSelect(teacherName);
            }}
            role="option"
            aria-selected={selectedTeacher === teacherName}
          >
            {teacherName}
          </li>
        )) : (
          <li className="px-3 py-2 text-sm text-gray-400 italic">No teachers found</li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <>
      {/* Mobile View - Show mobile filter buttons on small screens */}
      <div className="block md:hidden">
        <MobileFilterButtons
          selectedTeacher={selectedTeacher}
          selectedGroups={selectedGroups}
          onOpenStudentModal={handleOpenStudentModal}
          onOpenTeacherModal={handleOpenTeacherModal}
          teachers={teacherOptions}
          groups={groupOptions}
          showTeacher={showTeacher}
          showStudent={showGroups && !(isPublicView && isStudentsBuild())}
          compact={compactMobile}
        />
      </div>

      {/* Desktop View - Show traditional filters on medium screens and up */}
      <div className="hidden md:flex w-full items-center justify-center gap-3">
        {/* Teacher Filter - Label Removed */}
        {showTeacher && (
          <div className="w-[250px]">
            {complexFiltersActive ? (
              <div className={modernReadonlyControlClass}>
                {getComplexFilterText()}
              </div>
            ) : (
              <div className="relative" ref={teacherDropdownRef}>
                <button
                  id="teacher-filter"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isTeacherDropdownOpen) {
                      updateTeacherPanelPosition();
                    }
                    setIsTeacherDropdownOpen(prev => !prev);
                  }}
                  className={modernFilterButtonClass}
                  aria-haspopup="listbox"
                  aria-expanded={isTeacherDropdownOpen}
                  aria-label="Filter by Teacher"
                  ref={teacherTriggerRef}
                >
                  <span className="truncate">{selectedTeacher || 'All Teachers'}</span>
                  <span className={`text-gray-400 transition-transform ${isTeacherDropdownOpen ? 'rotate-180' : ''}`}>
                    <ChevronDownIcon />
                  </span>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Course Filter - Only show for admin view */}
        {showCourse && (
          <div className="w-[250px]">
            <div className="relative">
              <select
                id="course-filter"
                value={selectedCourse || ''}
                onChange={handleCourseChange}
                className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base min-h-[48px] appearance-none"
                aria-label="Filter by Course"
              >
                <option value="">All Courses</option>
                {courseOptions.filter(c => c).map(course => {
                  const courseName = typeof course === 'string' ? course : course?.name || 'Unknown';
                  const courseKey = typeof course === 'string' ? course : course?.id || course?.name || 'Unknown';
                  return <option key={courseKey} value={courseName}>{courseName}</option>;
                })}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDownIcon />
              </div>
            </div>
          </div>
        )}

        {/* Group Filter - Conditional rendering based on view mode */}
        {showGroups && (
          <div className="w-[250px]">
            {isPublicView ? (
              // Single-select dropdown for public view
              <div className="relative">
                <select
                  id="group-filter"
                  value={selectedGroups?.[0] || ''}
                  onChange={(e) => handleGroupsChange(e.target.value)}
                  className={modernFilterControlClass}
                  aria-label="Filter by Group"
                >
                  <option value="">All Groups</option>
                  {groupOptions.filter(g => g).map(group => {
                    const groupName = typeof group === 'string' ? group : group?.name || 'Unknown';
                    const groupKey = typeof group === 'string' ? group : group?.id || group?.name || 'Unknown';
                    return <option key={groupKey} value={groupName}>{groupName}</option>;
                  })}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 pointer-events-none">
                  <ChevronDownIcon />
                </div>
              </div>
            ) : (
              // Multi-select dropdown for admin view
              <>
                <span id="group-filter-label" className="sr-only">Filter by Group(s)</span>
                <MultiSelectDropdown
                  options={groupOptions.filter(g => g).map(group => typeof group === 'string' ? group : group?.name || 'Unknown')}
                  selectedOptions={selectedGroups}
                  onChange={handleGroupsChange}
                  placeholder="All Groups"
                  aria-labelledby="group-filter-label"
                />
              </>
            )}
          </div>
        )}

        {/* Action Buttons - Pushed to the right */}
        <div className="flex items-center gap-2">
          {/* Complex Filter Button - Hide only for students view */}
          {!isStudentsBuild() && (
            <button
              onClick={handleOpenComplexFilter}
              className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
              aria-label="Open advanced filters"
              title="Advanced Filters"
            >
              <HiAdjustmentsHorizontal className="h-5 w-5" />
            </button>
          )}

          {/* Clear Button */}
          <button
            onClick={handleClearFilters}
            className="p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 rounded-full"
            aria-label="Clear all filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Teacher Desktop Dropdown Panel */}
      {teacherDropdownPanel && createPortal(teacherDropdownPanel, document.body)}

      {/* Mobile Modals */}
      <StudentGroupSelectionModal
        isOpen={isStudentModalOpen}
        onClose={handleCloseStudentModal}
        groups={groupOptions}
        selectedGroups={selectedGroups}
        onGroupSelect={handleStudentGroupSelect}
      />

      <TeacherSelectionModal
        isOpen={isTeacherModalOpen}
        onClose={handleCloseTeacherModal}
        teachers={teacherOptions}
        selectedTeacher={selectedTeacher}
        onTeacherSelect={handleTeacherSelect}
      />
    </>
  );
}

export default ScheduleFilter;